import { useState, useEffect } from 'react';
import type { CycleRing } from '@/lib/cycleTypes';
import { PRESET_CYCLES } from '@/lib/cycleConfig';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Sparkles, Palette, Trash2, Download, Upload } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Switch } from '@/components/ui/switch';

interface CycleWizardProps {
  open: boolean;
  onClose: () => void;
  onCreateCycle: (cycle: CycleRing) => void;
  onUpdateCycle?: (cycle: CycleRing) => void;
  onDeleteCycle?: (cycleId: string) => void;
  editingCycle?: CycleRing | null;
  dayOfYear: number;
  allCycles?: CycleRing[];
  onImportCycles?: (cycles: CycleRing[]) => void;
}

export function CycleWizard({ 
  open, 
  onClose, 
  onCreateCycle, 
  onUpdateCycle,
  onDeleteCycle,
  editingCycle,
  dayOfYear,
  allCycles = [],
  onImportCycles,
}: CycleWizardProps) {
  const [step, setStep] = useState<'choose' | 'preset' | 'custom'>('choose');
  const [customCycle, setCustomCycle] = useState({
    name: '',
    stages: 12,
    labels: '',
    baseHue: 200,
    parentId: '' as string | undefined,
    waveform: 'default' as CycleRing['waveform'],
    stagesPerParent: '' as string | number | undefined,
    waveformIntervalMultiplier: '' as string | number | undefined,
    waveformIntervalDivider: '' as string | number | undefined,
  });
  const [parentStagesPerChild, setParentStagesPerChild] = useState('');

  // When editing, pre-populate the form
  useEffect(() => {
    if (editingCycle) {
      const safeName = String(editingCycle?.name ?? '');
      const safeStages = Number(editingCycle.stages) || 2;
      const safeLabels = Array.isArray(editingCycle.labels)
        ? editingCycle.labels.join(', ')
        : Array(safeStages).fill(0).map((_, i) => String(i + 1)).join(', ');
      const firstColor = editingCycle.colors && editingCycle.colors[0];
      const parsedHue = typeof firstColor === 'string'
        ? parseInt((firstColor.split && firstColor.split(' ')[0]) || '', 10)
        : NaN;
      const safeBaseHue = Number.isFinite(parsedHue) ? parsedHue : 200;

      setCustomCycle({
        name: safeName,
        stages: safeStages,
        labels: safeLabels,
        baseHue: safeBaseHue,
        parentId: editingCycle.parentId,
        stagesPerParent: editingCycle.stagesPerParent ?? '',
        waveform: editingCycle.waveform ?? 'default',
        waveformIntervalMultiplier: editingCycle.waveformIntervalMultiplier ?? '',
        waveformIntervalDivider: editingCycle.waveformIntervalDivider ?? '',
      });
      if (editingCycle.stagesPerParent && editingCycle.stagesPerParent > 0) {
        setParentStagesPerChild(String(1 / editingCycle.stagesPerParent));
      } else {
        setParentStagesPerChild('');
      }
      setStep('custom');
    } else {
      setCustomCycle({ 
        name: '', 
        stages: 12, 
        labels: '', 
        baseHue: 200, 
        parentId: undefined, 
        stagesPerParent: '', 
        waveform: 'default',
        waveformIntervalMultiplier: '',
        waveformIntervalDivider: '',
      });
      setParentStagesPerChild('');
      setStep('choose');
    }
  }, [editingCycle, open]);

  const handlePresetSelect = (presetId: string) => {
    const preset = PRESET_CYCLES[presetId];
    if (!preset) return;
    
    const currentStage = dayOfYear % preset.stages;
    const cycle: CycleRing = {
      ...preset,
      id: `${preset.id}-${Date.now()}`,
      currentStage,
      rotation: -currentStage * (360 / preset.stages),
    };
    
    onCreateCycle(cycle);
    handleClose();
  };

  const handleClose = () => {
    onClose();
    setStep('choose');
    setCustomCycle({ 
      name: '', 
      stages: 12, 
      labels: '', 
      baseHue: 200, 
      parentId: undefined, 
      stagesPerParent: '',
      waveform: 'default',
      waveformIntervalMultiplier: '',
      waveformIntervalDivider: '',
    });
    setParentStagesPerChild('');
  };

  const parseLabels = (labelString: string, stageCount: number): string[] => {
    if (!labelString.trim()) {
      return Array(stageCount).fill(0).map((_, i) => String(i + 1));
    }
    const parsed = labelString.split(',').map(s => s.trim()).filter(Boolean);
    if (parsed.length === stageCount) {
      return parsed;
    }
    // Pad or truncate to match stage count
    return Array(stageCount).fill(0).map((_, i) => parsed[i] || String(i + 1));
  };

  const handleSave = () => {
    if (!customCycle.name || customCycle.stages < 2) return;
    
    const labels = parseLabels(customCycle.labels, customCycle.stages);
    
    const colors = Array(customCycle.stages).fill(0).map((_, i) => 
      `${(customCycle.baseHue + (i * (360 / customCycle.stages))) % 360} 60% 50%`
    );
    
    const currentStage = dayOfYear % customCycle.stages;
    
    const hasSameStageCount = editingCycle?.stages === customCycle.stages;
    const rotation = hasSameStageCount
      ? editingCycle?.rotation ?? -currentStage * (360 / customCycle.stages)
      : -currentStage * (360 / customCycle.stages);
    const cycle: CycleRing = {
      ...(editingCycle ?? {}),
      id: editingCycle?.id || `custom-${Date.now()}`,
      name: customCycle.name,
      stages: customCycle.stages,
      labels,
      colors,
      currentStage,
      rotation,
      parentId: customCycle.parentId,
      waveform: customCycle.waveform ?? 'default',
      ...(customCycle.stagesPerParent !== '' && customCycle.stagesPerParent !== undefined
        ? { stagesPerParent: Number(customCycle.stagesPerParent) }
        : {}),
      ...(customCycle.waveformIntervalMultiplier !== '' && customCycle.waveformIntervalMultiplier !== undefined
        ? { waveformIntervalMultiplier: Number(customCycle.waveformIntervalMultiplier) }
        : {}),
      ...(customCycle.waveformIntervalDivider !== '' && customCycle.waveformIntervalDivider !== undefined
        ? { waveformIntervalDivider: Number(customCycle.waveformIntervalDivider) }
        : {}),
    };
    
    if (editingCycle && onUpdateCycle) {
      try {
        onUpdateCycle(cycle);
      } catch (err) {
        console.error('onUpdateCycle error', err, cycle);
        toast.error('Failed to update cycle');
        return;
      }
    } else {
      try {
        onCreateCycle(cycle);
      } catch (err) {
        console.error('onCreateCycle error', err, cycle);
        toast.error('Failed to create cycle');
        return;
      }
    }
    handleClose();
  };

  const handleDelete = () => {
    if (editingCycle && onDeleteCycle) {
      try {
        onDeleteCycle(editingCycle.id);
        handleClose();
      } catch (err) {
        console.error('onDeleteCycle error', err, editingCycle);
        toast.error('Failed to delete cycle');
      }
    }
  };

  const selectedParent = allCycles.find(c => c.id === customCycle.parentId);
  const parentStagesPerChildValue = Number(parentStagesPerChild);
  const helperStagesPerParent = parentStagesPerChildValue > 0 ? 1 / parentStagesPerChildValue : null;
  const stageRatioMismatch = helperStagesPerParent !== null
    && customCycle.stagesPerParent !== ''
    && customCycle.stagesPerParent !== undefined
    && Number.isFinite(Number(customCycle.stagesPerParent))
    && Math.abs(Number(customCycle.stagesPerParent) - helperStagesPerParent) > 1e-4;

  const handleExport = () => {
    if (allCycles.length === 0) {
      toast.error('No cycles to export');
      return;
    }
    
    const exportData = {
      version: 1,
      exportedAt: new Date().toISOString(),
      cycles: allCycles.map(c => ({
        id: c.id,
        name: c.name,
        stages: c.stages,
        labels: c.labels,
        colors: c.colors,
        parentId: c.parentId,
        stagesPerParent: c.stagesPerParent,
        waveform: c.waveform,
        waveformIntervalMultiplier: c.waveformIntervalMultiplier,
        waveformIntervalDivider: c.waveformIntervalDivider,
      })),
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cycle-config-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${allCycles.length} cycles`);
  };

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string) as unknown;
        let raw: unknown[] | null = null;
        if (data && typeof data === 'object') {
          const obj = data as Record<string, unknown>;
          const maybeCycles = obj['cycles'];
          if (Array.isArray(maybeCycles)) raw = maybeCycles as unknown[];
        }
        if (!raw) {
          throw new Error('Invalid format');
        }

        const importedCycles: CycleRing[] = raw.map((c: unknown) => {
          const item = c as Partial<CycleRing & { id?: string; name?: string; labels?: string[]; colors?: string[]; stagesPerParent?: number }>;
          const stages = Number(item.stages) || 1;
          const labels = Array.isArray(item.labels) ? item.labels : Array.from({ length: stages }).map((_, i) => String(i + 1));
          const colors = Array.isArray(item.colors) ? item.colors : Array(stages).fill(`${200} 60% 50%`);

          return {
            id: `imported-${item.id ?? 'cycle'}-${Date.now()}`,
            name: String(item.name ?? 'Imported Cycle'),
            stages,
            labels,
            colors,
            currentStage: dayOfYear % stages,
            rotation: -(dayOfYear % stages) * (360 / stages),
            parentId: item.parentId,
            stagesPerParent: item.stagesPerParent,
            waveform: item.waveform,
            waveformIntervalMultiplier: item.waveformIntervalMultiplier,
            waveformIntervalDivider: item.waveformIntervalDivider,
          };
        });

        if (onImportCycles) {
          onImportCycles(importedCycles);
          toast.success(`Imported ${importedCycles.length} cycles`);
          handleClose();
        }
      } catch (err) {
        toast.error('Failed to import: Invalid JSON format');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-w-[95vw] bg-card border-border p-2 sm:p-6 overflow-y-auto" style={{ maxHeight: '90vh' }}>
        <DialogHeader>
          <DialogTitle className="font-display">
            {editingCycle ? 'Edit Cycle' : (
              step === 'choose' ? 'Add Cycle Ring' :
              step === 'preset' ? 'Choose Preset Cycle' :
              'Create Custom Cycle'
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Configure cycle ring properties, relationships, and appearance.
          </DialogDescription>
        </DialogHeader>
        
        {step === 'choose' && !editingCycle && (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setStep('preset')}
                className="flex flex-col items-center gap-3 p-6 rounded-xl border border-border hover:border-primary hover:bg-primary/5 transition-all"
              >
                <Sparkles className="w-8 h-8 text-primary" />
                <div className="text-center">
                  <p className="font-medium">Use Preset</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Choose from traditional cycles
                  </p>
                </div>
              </button>
              
              <button
                onClick={() => setStep('custom')}
                className="flex flex-col items-center gap-3 p-6 rounded-xl border border-border hover:border-accent hover:bg-accent/5 transition-all"
              >
                <Palette className="w-8 h-8 text-accent" />
                <div className="text-center">
                  <p className="font-medium">Create Custom</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Define your own cycle
                  </p>
                </div>
              </button>
            </div>
            
            <div className="border-t border-border pt-4">
              <p className="text-xs text-muted-foreground mb-3 text-center">Import/Export Configurations</p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" size="sm" onClick={handleExport} className="gap-1.5">
                  <Download className="w-4 h-4" />
                  Export All
                </Button>
                <label>
                  <Button variant="outline" size="sm" asChild className="gap-1.5 cursor-pointer">
                    <span>
                      <Upload className="w-4 h-4" />
                      Import
                    </span>
                  </Button>
                  <input type="file" accept=".json" onChange={handleImport} className="hidden" />
                </label>
              </div>
            </div>
          </div>
        )}
        
        {step === 'preset' && (
          <ScrollArea className="h-[300px] pr-4">
            <div className="space-y-2">
              {Object.entries(PRESET_CYCLES).map(([id, preset]) => (
                <button
                  key={id}
                  onClick={() => handlePresetSelect(id)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all text-left"
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-lg"
                    style={{ backgroundColor: `hsl(${preset.colors[0]} / 0.3)` }}
                  >
                    {preset.labels[0]}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{preset.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {preset.stages} stages
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        )}
        
        {step === 'custom' && (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cycle-name">Cycle Name</Label>
                <Input
                  id="cycle-name"
                  placeholder="e.g., Lunar Phases"
                  value={customCycle.name}
                  onChange={(e) => setCustomCycle(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-background"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="cycle-stages">Number of Stages</Label>
                <Input
                  id="cycle-stages"
                  type="number"
                  min={2}
                  max={365}
                  value={customCycle.stages}
                  onChange={(e) => setCustomCycle(prev => ({ 
                    ...prev, 
                    stages: parseInt(e.target.value) || 2 
                  }))}
                  className="bg-background"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="waveform-select">Cycle Waveform</Label>
              <select
                id="waveform-select"
                value={customCycle.waveform}
                onChange={(e) => setCustomCycle(prev => ({ ...prev, waveform: e.target.value as CycleRing['waveform'] }))}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="default">Default (linear)</option>
                <option value="sine">Sine</option>
                <option value="triangle">Triangle</option>
                <option value="square">Square</option>
                <option value="sawtooth">Sawtooth</option>
                <option value="24hr">Legacy 24hr Sine</option>
              </select>
              <p className="text-xs text-muted-foreground">Choose how this cycle oscillates in the waveform view.</p>
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="space-y-2">
                  <Label htmlFor="waveform-multiplier">Interval Multiplier</Label>
                  <Input
                    id="waveform-multiplier"
                    type="number"
                    min={0.01}
                    step="0.01"
                    value={customCycle.waveformIntervalMultiplier ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCustomCycle(prev => ({ ...prev, waveformIntervalMultiplier: v === '' ? '' : parseFloat(v) }));
                    }}
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="waveform-divider">Interval Divider</Label>
                  <Input
                    id="waveform-divider"
                    type="number"
                    min={0.01}
                    step="0.01"
                    value={customCycle.waveformIntervalDivider ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setCustomCycle(prev => ({ ...prev, waveformIntervalDivider: v === '' ? '' : parseFloat(v) }));
                    }}
                    className="bg-background"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="parent-id">Parent (timeline or ring, optional)</Label>
                <Select
                  value={customCycle.parentId ?? '__none'}
                  onValueChange={(v) => setCustomCycle(prev => ({ ...prev, parentId: v === '__none' ? undefined : v }))}
                >
                  <SelectTrigger className="w-full bg-background">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">None</SelectItem>
                    <SelectItem value="__timeline__">Timeline (Gregorian day)</SelectItem>
                    {allCycles.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name} ({c.stages})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="stages-per-parent">Stages per parent (optional)</Label>
                <Input
                  id="stages-per-parent"
                  type="number"
                  step="0.01"
                  min={0.0001}
                  value={customCycle.stagesPerParent ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCustomCycle(prev => ({ ...prev, stagesPerParent: v === '' ? '' : parseFloat(v) }));
                  }}
                  className="bg-background"
                />
                <p className="text-xs text-muted-foreground">Use decimals for fractional ratios (e.g., 0.5)</p>
                {customCycle.parentId === '__timeline__' && (
                  <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-xs space-y-2">
                    <p className="font-medium text-foreground">Timeline helper</p>
                    <p className="text-muted-foreground">
                      Timeline uses the Gregorian day-of-year (365 days, 366 on leap years). Enter how many child stages occur per
                      Gregorian day. Examples: 1 = one stage per day; 28 = full 28-stage cycle per day; 0.5 = one stage every two days.
                    </p>
                  </div>
                )}
                {customCycle.parentId && customCycle.parentId !== '__timeline__' && selectedParent && (
                  <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-xs space-y-2">
                    <p className="font-medium text-foreground">Helper</p>
                    <p className="text-muted-foreground">
                      Enter how many parent stages fit into 1 child stage. Example: if 1 child stage equals a full parent cycle,
                      enter {selectedParent.stages}.
                    </p>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0.0001}
                        step="0.01"
                        value={parentStagesPerChild}
                        onChange={(e) => setParentStagesPerChild(e.target.value)}
                        className="h-8 bg-background text-xs"
                      />
                      <span className="text-muted-foreground">parent stages per child stage</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-muted-foreground">
                      <span>
                        Calculated stages per parent:{' '}
                        <span className="font-semibold text-foreground">
                          {helperStagesPerParent ? helperStagesPerParent.toFixed(6) : '—'}
                        </span>
                      </span>
                      <Button
                        type="button"
                        variant={stageRatioMismatch ? 'destructive' : 'ghost'}
                        size="sm"
                        disabled={!helperStagesPerParent}
                        onClick={() => {
                          if (!helperStagesPerParent) return;
                          setCustomCycle(prev => ({ ...prev, stagesPerParent: Number(helperStagesPerParent.toFixed(6)) }));
                        }}
                      >
                        Use value
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="cycle-labels">Stage Labels (comma-separated, optional)</Label>
              <Textarea
                id="cycle-labels"
                placeholder="e.g., New, Waxing, Full, Waning"
                value={customCycle.labels}
                onChange={(e) => setCustomCycle(prev => ({ ...prev, labels: e.target.value }))}
                className="bg-background h-20 resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Leave empty for numbered stages. Provide exactly {customCycle.stages} labels for custom names.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Base Color Hue (0-360)</Label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={0}
                  max={360}
                  value={customCycle.baseHue}
                  onChange={(e) => setCustomCycle(prev => ({ 
                    ...prev, 
                    baseHue: parseInt(e.target.value) 
                  }))}
                  className="flex-1"
                />
                <div 
                  className="w-8 h-8 rounded-full border border-border"
                  style={{ backgroundColor: `hsl(${customCycle.baseHue} 60% 50%)` }}
                />
              </div>
            </div>
            
            <div className="pt-2">
              <p className="text-xs text-muted-foreground mb-2">
                Preview: {customCycle.stages} stages
              </p>
              <div className="flex gap-1 flex-wrap">
                {Array(Math.min(customCycle.stages, 20)).fill(0).map((_, i) => (
                  <div
                    key={i}
                    className="w-4 h-4 rounded-sm"
                    style={{ 
                      backgroundColor: `hsl(${(customCycle.baseHue + (i * (360 / customCycle.stages))) % 360} 60% 50%)` 
                    }}
                  />
                ))}
                {customCycle.stages > 20 && (
                  <span className="text-xs text-muted-foreground">+{customCycle.stages - 20} more</span>
                )}
              </div>
            </div>
          </div>
        )}
        
        <DialogFooter className="flex justify-between">
          <div>
            {editingCycle && onDeleteCycle && (
              <Button variant="destructive" onClick={handleDelete} className="gap-1">
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {step !== 'choose' && !editingCycle && (
              <Button variant="ghost" onClick={() => setStep('choose')}>
                Back
              </Button>
            )}
            {(step === 'custom' || editingCycle) && (
              <Button onClick={handleSave} disabled={!customCycle.name}>
                {editingCycle ? 'Update Cycle' : 'Create Cycle'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
